import { isCustomerFormShowNotFound } from './customerFormId';

/**
 * Client-side Axios failures that are not actionable app bugs.
 * Keep in sync with CRM `isNoiseAxiosError` (CU-86d3vb6hq), plus Book-specific
 * expected tenant-resolution 404s (MOVEPRO-BOOK-AC) and missing-form GETs
 * (MOVEPRO-BOOK-AN / MOVEPRO-BOOK-AW — FillForm routes those to FormUnavailable).
 *
 * ECONNABORTED covers a browser-aborted request and a request timeout, which
 * axios gives the same code. The abort is noise and was reaching Sentry:
 * MOVEPRO-BOOK-R (PaymentPage, 16,791 events) and MOVEPRO-BOOK-3W (/csrf).
 * Verified in Chrome as identical on axios 1.19 and 1.20, so it is not
 * version-specific. A timeout is a real availability signal and must keep
 * reporting (CU-86d477a1g).
 *
 * The two are separated on axios's message, not on `config.timeout`. That
 * looks like the cleaner discriminator and is wrong: axios emits the bare
 * 'timeout exceeded' precisely WHEN `config.timeout` is falsy
 * (adapters/xhr.js, `request.ontimeout`), and `ontimeout` does fire with no
 * timeout configured — MOVEPRO-CRM-E3 is 1,195 such login timeouts across 62
 * users. Testing `!config.timeout` would classify every one of them as noise.
 *
 * Only axios's exact `onabort` message counts as noise, so any ECONNABORTED
 * we do not recognise keeps reporting. That fails safe: an unrecognised
 * transport error becomes a Sentry issue rather than silence.
 */
const ABORTED_MESSAGE = 'Request aborted';

/**
 * The API refusing a booking-form write because the move is already confirmed
 * (CU-86d4at562). A 409 carrying the step to send the customer to is a handled,
 * expected state — the customer was mid-funnel when the job was booked — not an
 * app bug, so it must not open a Sentry issue.
 *
 * Keyed on the `form_step` in the body as well as the status, so an unrelated
 * 409 from anywhere else keeps reporting.
 */
export function isBookingConfirmedConflict(error: unknown): boolean {
    const e = error as any;

    return e?.response?.status === 409 && typeof e?.response?.data?.form_step === 'string';
}

export function isNoiseAxiosError(error: unknown): boolean {
    const e = error as any;

    if (e?.code === 'ERR_CANCELED' || e?.code === 'ERR_NETWORK' || e?.message === 'Network Error') {
        return true;
    }

    if (e?.code === 'ECONNABORTED' && e?.message === ABORTED_MESSAGE) {
        return true;
    }

    if (isBookingConfirmedConflict(error)) {
        return true;
    }

    // Path/host tenancy miss: API returns 404 {"message":"Tenant not found."}
    // before any customer route runs. Expected on typos / reserved subdomains.
    if (
        e?.response?.status === 404 &&
        typeof e?.response?.data?.message === 'string' &&
        e.response.data.message.startsWith('Tenant not found')
    ) {
        return true;
    }

    // FillForm already sends the customer to FormUnavailable. The 404 is the
    // expected missing/mangled-id outcome, not an unhandled app bug.
    if (isCustomerFormShowNotFound(e)) {
        return true;
    }

    return false;
}
